import React from 'react';

import { FormProvider, useForm } from 'react-hook-form';

import { LbBigModal } from '../../../../../../shared/components/modals';
import { socialSharingScanMetricsService } from '../../../../../services/socialSharingScanMetricsService';
import { DateRangeSection } from './DateRangeSection/DateRangeSection';
import { MetricsSection } from './MetricsSection/MetricsSection';
import { BackgroundSection } from './BackgroundSection/BackgroundSection';
import { useBgOptions } from './hooks/useBgOptions';
import { ScanMetadata } from '../../../../../interfaces/scanMetadata';
import { SocialSharingScanMetricsFormValues } from './SocialMediaScanMetricsModal.settings';
import {
  CUSTOMER_TYPE,
  CustomerType,
} from '../../../../../../shared/constants/customer/customerType';
import { SocialSharingScanMetricsParams } from '../../../../../interfaces/socialSharingScanMetrics';

type SocialMediaScanMetricsModalProps = {
  customerId: number;
  entityId?: number;
  scansMetadata: ScanMetadata[];
  customerType: CustomerType;
  onClose: () => void;
};

const SocialMediaScanMetricsModal = ({
  customerId,
  entityId,
  scansMetadata,
  customerType,
  onClose,
}: SocialMediaScanMetricsModalProps) => {
  const { bgOptions } = useBgOptions();

  const formMethods = useForm<SocialSharingScanMetricsFormValues>();
  const {
    handleSubmit,
    watch,
    formState: { isValid },
  } = formMethods;

  const metrics = watch('metrics'); // null on initial render
  const isMetricSelected = !!metrics && Object.values(metrics).some(Boolean);

  const onSubmit = handleSubmit(({ startScanId, endScanId, ...data }) => {
    const startScan = scansMetadata.find((scan) => scan.scanId === startScanId);
    const endScan = scansMetadata.find((scan) => scan.scanId === endScanId);

    if (!startScan || !endScan) {
      throw new Error('Start or end scan not found');
    }

    processDownloadTemplate({
      startDate: startScan.scanDateTime,
      endDate: endScan.scanDateTime,
      ...data,
    });
    onClose();
  });

  const processDownloadTemplate = async (params: SocialSharingScanMetricsParams) => {
    if (customerType === CUSTOMER_TYPE.SCAN_IQ) {
      await socialSharingScanMetricsService.scanIqDownloadTemplate(
        customerId,
        params,
        entityId,
      );
    } else {
      await socialSharingScanMetricsService.downloadTemplate(customerId, params);
    }
  };

  return (
    <LbBigModal
      open={bgOptions.length > 0}
      title="Create and Download Social Media Template"
      description="Share your client's success"
      buttonOptions={{
        noText: 'Cancel',
        noHandler: onClose,
        yesText: 'Download',
        yesHandler: onSubmit,
        yesDisabled: !isMetricSelected || !isValid,
      }}
    >
      <FormProvider {...formMethods}>
        <DateRangeSection scansMetadata={scansMetadata} />
        <MetricsSection />
        <BackgroundSection bgOptions={bgOptions} />
      </FormProvider>
    </LbBigModal>
  );
};

export { SocialMediaScanMetricsModal };
